import React, { useState } from 'react'
import { isCallId } from '../utils/callLinks'
import { ROUTES } from './navigation'

const STAGES = [
  ['endOfTurnMs', 'End of turn', 'Estimated last word to finalized input. Unavailable when the provider has no usable word timing.'],
  ['modelFirstOutputMs', 'Model first output', 'First model request to its first text or tool-argument output, including network time.'],
  ['firstTextMs', 'Input to first text', 'Finalized input to the first spoken-text fragment. Includes any tools before that text.'],
  ['synthesisMs', 'Speech generation', 'First speech request to first audio sent to the phone provider.'],
  ['toolMs', 'Tools', 'Total tool execution time across the turn, including tools after a progress phrase.'],
  ['readyToVoiceMs', 'Input to voice', 'Finalized input to first audio sent to the phone provider.'],
  ['totalResponseMs', 'Total response', 'Estimated last word to first audio sent to the phone provider.'],
]

const duration = value => Number.isFinite(value)
  ? (value >= 1000 ? `${(value / 1000).toFixed(2)} s` : `${Math.round(value)} ms`)
  : 'Unavailable'

const REJECTION_LABELS = {
  internal_speech: 'internal wording', unsupported_claim: 'unsupported claim',
  message_additions: 'message details', repeated_offer: 'repeated offer',
  missing_handoff: 'missing handoff', validation_error: 'validation error',
  unselected_path: 'unselected path', unanswered_question: 'unanswered question',
  callback_offer: 'callback offer', required_fields: 'required fields',
  repeated_collection: 'repeated collection',
}

function TurnContext({ turn }) {
  const notes = []
  if (turn.toolCount > 0) notes.push(`${turn.toolCount} ${turn.toolCount === 1 ? 'tool' : 'tools'}: ${duration(turn.metrics?.toolMs)}`)
  if (turn.validationCount > 0) notes.push(`Validation: ${turn.validationCount}, ${duration(turn.metrics?.validationMs)}`)
  const rejections = (turn.validationRejections || []).map(reason => REJECTION_LABELS[reason]).filter(Boolean)
  if (rejections.length) notes.push(`Draft held: ${rejections.join(', ')}`)
  if (turn.modelRequests > 1) notes.push(`${turn.modelRequests} model requests recorded`)
  if (turn.metrics?.voiceReadyToSendMs > 250) notes.push(`Audio ready before send: ${duration(turn.metrics.voiceReadyToSendMs)}`)
  return <div className="call-turn-context">{notes.length ? notes.map(note => <span key={note}>{note}</span>) : <span>No tool or validation delay recorded</span>}</div>
}

function CallLatencyPanel({ data }) {
  const [query, setQuery] = useState('')
  const [phase, setPhase] = useState('all')
  const turns = (data?.recentTurns || []).filter(turn =>
    (phase === 'all' || turn.phase === phase) &&
    `${turn.callId || ''} ${turn.runId || ''}`.toLowerCase().includes(query.trim().toLowerCase()))
  const trend = data?.trend || []
  const targetMs = data?.targetMs || 1500
  const scaleMs = Math.max(targetMs * 1.25, ...trend.map(bucket => bucket.p95Ms || 0))
  const trendCount = trend.reduce((sum, bucket) => sum + bucket.count, 0)
  const withinTarget = trend.reduce((sum, bucket) => sum + bucket.withinTarget, 0)

  return (
    <article className="observability-card">
      <div className="observability-section-heading">
        <div><h3>Call response timing</h3><p>Opening responses and later turns in the selected time window.</p></div>
        <span>{data?.retainedTurns || 0} retained turns</span>
      </div>
      <p>Each summary shows average / p95 for completed responses. Missing measurements are unavailable, never zero.
        Stages overlap and should not be added. First audio includes progress phrases and measures when audio is sent, not heard.</p>
      <div className="call-latency-trend-heading">
        <div><h4>Caller to first AI audio over time</h4><p>From estimated last spoken word to audio sent to the phone provider. Updates every 30 seconds while this view is open.</p></div>
        <strong>{trendCount ? `${Math.round(withinTarget / trendCount * 100)}% within ${duration(targetMs)}` : 'No measured responses yet'}</strong>
      </div>
      {trendCount ? <>
        <div className="call-latency-trend-legend"><span className="median">Median</span><span className="p95">95th percentile</span><span className="target">Target: {duration(targetMs)}</span></div>
        <div className="call-latency-trend" role="img" aria-label={`Caller to first audio trend. ${trendCount} measured responses, ${withinTarget} within ${duration(targetMs)}.`}>
          <div className="call-latency-target" style={{ bottom: `calc(22px + ${targetMs / scaleMs * 160}px)` }} />
          {trend.map(bucket => <div className="call-latency-bucket" key={bucket.timestamp} title={`${new Date(bucket.timestamp).toLocaleString()}: ${bucket.count} turns, median ${duration(bucket.medianMs)}, p95 ${duration(bucket.p95Ms)}`}>
            <div className="call-latency-bars">{bucket.count ? <><span className="median" style={{ height: `${bucket.medianMs / scaleMs * 100}%` }} /><span className="p95" style={{ height: `${bucket.p95Ms / scaleMs * 100}%` }} /></> : null}</div>
            <small>{new Date(bucket.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</small>
          </div>)}
        </div>
        <p className="call-latency-trend-note">{trendCount} measured responses in the selected window{data?.limitReached ? ' from the most recent retained results' : ''}. Hover over a bar for its time, count, and values.</p>
      </> : <div className="admin-operations-empty">No completed responses with timing in this window.</div>}
      {!data?.retainedTurns ? (
        <div className="admin-operations-empty">No call response measurements retained in this window. New calls populate these timings; older calls have no stage breakdown.</div>
      ) : <>
        <div className="observability-table-wrap">
          <table className="observability-table" aria-label="Call timing averages and p95">
            <thead><tr><th>Response</th><th>Completed / retained</th>{STAGES.map(([key, label, detail]) => <th key={key} title={detail}>{label}</th>)}</tr></thead>
            <tbody>{(data.groups || []).map(group => <tr key={group.phase}>
              <td>{group.phase === 'opening' ? 'Opening response' : 'Later responses'}</td>
              <td>{group.completed} / {group.turns}</td>
              {STAGES.map(([key]) => {
                const metric = group.metrics?.[key]
                return <td key={key} title={`${metric?.count || 0} measured responses`}>
                  {metric?.count ? `${duration(metric.averageMs)} / ${duration(metric.p95Ms)}` : 'Unavailable'}
                </td>
              })}
            </tr>)}</tbody>
          </table>
        </div>
        <div className="observability-section-heading observability-events-heading">
          <div><h4>Recent caller turns</h4><p>Up to 100 retained turns. A turn appears after the caller speaks again or the call ends.</p></div>
          <input aria-label="Filter call timings" placeholder="Call or trace ID" value={query} onChange={event => setQuery(event.target.value)} />
          <select aria-label="Call response phase" value={phase} onChange={event => setPhase(event.target.value)}>
            <option value="all">All responses</option><option value="opening">Opening response</option><option value="later">Later responses</option>
          </select>
        </div>
        <div className="observability-table-wrap">
          <table className="observability-table" aria-label="Individual call response timings">
            <thead><tr><th>Call / turn</th><th>Status</th><th>What happened</th>{STAGES.map(([key, label, detail]) => <th key={key} title={detail}>{label}</th>)}<th>Recorded</th></tr></thead>
            <tbody>{turns.map(turn => <tr key={`${turn.runId}-${turn.turnNumber}`}>
              <td>
                {isCallId(turn.callId)
                  ? <a href={`${ROUTES.calls}?callId=${encodeURIComponent(turn.callId)}`}>{turn.callId.slice(0, 10)}</a>
                  : <span>Call link unavailable</span>}
                <div>{turn.phase === 'opening' ? 'Opening' : `Turn ${turn.turnNumber}`}</div>
                <small>{turn.model || 'Model unavailable'} / {turn.sttProvider || 'Input provider unavailable'}</small>
              </td>
              <td>{turn.status === 'succeeded' ? 'Responded' : turn.status === 'interrupted' ? 'Interrupted before audio' : 'Ended without audio'}</td>
              <td className="call-turn-context-cell"><TurnContext turn={turn} /></td>
              {STAGES.map(([key]) => <td key={key}>{duration(turn.metrics?.[key])}</td>)}
              <td>{turn.timestamp ? new Date(turn.timestamp).toLocaleString() : 'Unavailable'}</td>
            </tr>)}</tbody>
          </table>
          {!turns.length && <p>No retained turns match these filters.</p>}
        </div>
      </>}
    </article>
  )
}

export default CallLatencyPanel
